import type { FieldKey } from './types';
import { normalizeText } from '../utils/text';

export interface FieldDefinition {
  readonly key: FieldKey;
  readonly label: string;
  readonly aliases: readonly string[];
  readonly required: boolean;
}

export const FIELD_DEFINITIONS: readonly FieldDefinition[] = [
  { key: 'sequence', label: '序号', aliases: ['序号', '项次', '顺序号'], required: false },
  {
    key: 'code',
    label: '项目编码',
    aliases: ['项目编码', '清单编码', '编码', '项目编号'],
    required: true,
  },
  {
    key: 'name',
    label: '项目名称',
    aliases: ['项目名称', '清单名称', '名称'],
    required: true,
  },
  {
    key: 'description',
    label: '项目特征',
    aliases: ['项目特征', '项目特征描述', '特征描述', '工作内容'],
    required: true,
  },
  {
    key: 'unit',
    label: '计量单位',
    aliases: ['计量单位', '单位'],
    required: true,
  },
  {
    key: 'quantity',
    label: '工程量',
    aliases: ['工程量', '数量', '清单工程量'],
    required: true,
  },
  {
    key: 'unitPrice',
    label: '综合单价',
    aliases: ['综合单价', '含税综合单价', '不含税综合单价', '单价'],
    required: true,
  },
  {
    key: 'amount',
    label: '合价',
    aliases: ['合价', '综合合价', '金额', '总价'],
    required: true,
  },
] as const;

export const REQUIRED_FIELD_KEYS = FIELD_DEFINITIONS.filter(({ required }) => required).map(
  ({ key }) => key,
);

export interface FieldAliasMatch {
  readonly field: FieldKey;
  readonly alias: string;
  readonly header: string;
  readonly confidence: number;
}

export function normalizeHeader(value: unknown): string {
  return normalizeText(value, { caseInsensitive: true, removeAllWhitespace: true })
    .replace(/[：:()（）【】]/gu, '')
    .replaceAll('[', '')
    .replaceAll(']', '');
}

export function matchFieldAlias(value: unknown): FieldAliasMatch | null {
  const header = normalizeHeader(value);
  if (header.length === 0) return null;

  const candidates: FieldAliasMatch[] = [];
  for (const definition of FIELD_DEFINITIONS) {
    for (const alias of definition.aliases) {
      const normalizedAlias = normalizeHeader(alias);
      if (header === normalizedAlias) {
        candidates.push({ field: definition.key, alias, header, confidence: 1 });
      } else if (normalizedAlias.length >= 2 && header.includes(normalizedAlias)) {
        candidates.push({ field: definition.key, alias, header, confidence: 0.82 });
      }
    }
  }

  candidates.sort((left, right) => {
    if (right.confidence !== left.confidence) return right.confidence - left.confidence;
    return normalizeHeader(right.alias).length - normalizeHeader(left.alias).length;
  });
  return candidates[0] ?? null;
}

export function getFieldDefinition(field: FieldKey): FieldDefinition {
  const definition = FIELD_DEFINITIONS.find(({ key }) => key === field);
  if (definition === undefined) throw new Error(`Unknown field: ${field}`);
  return definition;
}
